import React, { useState } from 'react';
import { supabase } from '../supabase';
import {
  CONTACT_KINDS, INTRODUCED_BY, RELATIONSHIP_TYPES,
  statusesForKind, defaultStatusForKind, todayIso, userDisplayName,
} from '../directory';

function emptyForm() {
  return {
    contact_kind: 'network',
    full_name: '',
    profession: '',
    company: '',
    city_area: '',
    email: '',
    phone: '',
    linkedin_url: '',
    first_met_date: todayIso(),
    first_met_channel: '',
    introduced_by: '',
    intro_detail: '',
    typical_sentence: '',
    relationship_type: 'can_bring_work',
    status: 'new',
    next_action: '',
    next_action_date: '',
    customer_id: '',
  };
}

function fromContact(c) {
  return {
    contact_kind: c.contact_kind || 'network',
    full_name: c.full_name || '',
    profession: c.profession || '',
    company: c.company || '',
    city_area: c.city_area || '',
    email: c.email || '',
    phone: c.phone || '',
    linkedin_url: c.linkedin_url || '',
    first_met_date: c.first_met_date || todayIso(),
    first_met_channel: c.first_met_channel || '',
    introduced_by: c.introduced_by || '',
    intro_detail: c.intro_detail || '',
    typical_sentence: c.typical_sentence || '',
    relationship_type: c.relationship_type || 'can_bring_work',
    status: c.status || 'new',
    next_action: c.next_action || '',
    next_action_date: c.next_action_date || '',
    customer_id: c.customer_id || '',
  };
}

const SECTION = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
  color: 'var(--text3)', margin: '4px 0 12px',
};

// Module scope — defining this inside the component would remount inputs on
// every keystroke and drop focus.
function Field({ label, children }) {
  return (
    <div>
      <label>{label}</label>
      {children}
    </div>
  );
}

export default function DirectoryContactModal({ contact, customers = [], user, onClose, onSaved }) {
  const isEdit = !!contact;
  const [form, setForm] = useState(() => (contact ? fromContact(contact) : emptyForm()));
  const [noteText, setNoteText] = useState('');
  const [noteDate, setNoteDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const setKind = (k) => {
    setForm(f => {
      const validStatus = Object.keys(statusesForKind(k)).includes(f.status);
      return { ...f, contact_kind: k, status: validStatus ? f.status : defaultStatusForKind(k) };
    });
  };

  const isNetwork = form.contact_kind === 'network';
  const statusOptions = statusesForKind(form.contact_kind);

  const save = async () => {
    const missing = [];
    if (!form.full_name.trim()) missing.push('Ad soyad');
    if (!form.profession.trim()) missing.push('Meslek');
    if (!form.first_met_date) missing.push('Tanışma tarihi');
    if (!form.introduced_by) missing.push('Tanıtan');
    if (!form.status) missing.push('Durum');
    if (isNetwork && !form.relationship_type) missing.push('Bize faydası');
    if (missing.length) {
      setError('Zorunlu alan eksik: ' + missing.join(', '));
      return;
    }
    setError('');
    setBusy(true);

    const payload = {
      contact_kind: form.contact_kind,
      full_name: form.full_name.trim(),
      profession: form.profession.trim(),
      company: form.company.trim() || null,
      city_area: form.city_area.trim() || null,
      email: form.email.trim() || null,
      phone: form.phone.trim() || null,
      linkedin_url: form.linkedin_url.trim() || null,
      first_met_date: form.first_met_date,
      first_met_channel: form.first_met_channel.trim() || null,
      introduced_by: form.introduced_by,
      intro_detail: form.intro_detail.trim() || null,
      typical_sentence: form.typical_sentence.trim() || null,
      relationship_type: isNetwork ? form.relationship_type : null,
      status: form.status,
      next_action: form.next_action.trim() || null,
      next_action_date: form.next_action_date || null,
      customer_id: form.customer_id || null,
    };

    if (isEdit) {
      const { data, error: e } = await supabase
        .from('directory_contacts')
        .update({ ...payload, updated_at: new Date().toISOString() })
        .eq('id', contact.id)
        .select()
        .single();
      setBusy(false);
      if (e) { setError('Kaydedilemedi: ' + e.message); return; }
      onSaved(data, { created: false });
      return;
    }

    // New contact — last_contact_date starts at the meeting date, bumped if the
    // optional first note is more recent.
    const hasNote = !!noteText.trim();
    let lastContact = form.first_met_date;
    if (hasNote && noteDate > lastContact) lastContact = noteDate;

    const { data, error: e } = await supabase
      .from('directory_contacts')
      .insert({ ...payload, last_contact_date: lastContact })
      .select()
      .single();
    if (e) { setBusy(false); setError('Kaydedilemedi: ' + e.message); return; }

    if (hasNote) {
      const { error: ne } = await supabase.from('directory_contact_notes').insert({
        contact_id: data.id,
        note_date: noteDate,
        note_text: noteText.trim(),
        created_by: userDisplayName(user),
      });
      if (ne) {
        setBusy(false);
        setError('Kişi kaydedildi ama ilk not eklenemedi: ' + ne.message);
        return;
      }
    }
    setBusy(false);
    onSaved(data, { created: true });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Kişiyi düzenle' : 'Yeni kişi'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-row cols-2">
            <Field label="Tür">
              <select value={form.contact_kind} onChange={e => setKind(e.target.value)}>
                {Object.entries(CONTACT_KINDS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Durum">
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                {Object.entries(statusOptions).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
          </div>

          <div style={SECTION}>Kimlik</div>
          <div className="form-row">
            <Field label="Ad soyad *">
              <input value={form.full_name} onChange={e => set('full_name', e.target.value)} />
            </Field>
          </div>
          <div className="form-row cols-2">
            <Field label={isNetwork ? 'Meslek *' : 'Meslek / dal *'}>
              <input value={form.profession} onChange={e => set('profession', e.target.value)}
                placeholder={isNetwork ? 'Mimar, Developer…' : 'Elektrikçi, Structural engineer…'} />
            </Field>
            <Field label="Firma">
              <input value={form.company} onChange={e => set('company', e.target.value)} />
            </Field>
          </div>
          <div className="form-row cols-2">
            <Field label={isNetwork ? 'Şehir / bölge' : 'Kapsadığı bölge'}>
              <input value={form.city_area} onChange={e => set('city_area', e.target.value)}
                placeholder="Hampstead, London" />
            </Field>
            <Field label="Müşteri kaydı (opsiyonel)">
              <select value={form.customer_id} onChange={e => set('customer_id', e.target.value)}>
                <option value="">— bağlı değil —</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.full_name}{c.company ? ` — ${c.company}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div style={SECTION}>İletişim</div>
          <div className="form-row cols-3">
            <Field label="Email">
              <input value={form.email} onChange={e => set('email', e.target.value)} />
            </Field>
            <Field label="Telefon">
              <input value={form.phone} onChange={e => set('phone', e.target.value)} />
            </Field>
            <Field label="LinkedIn">
              <input value={form.linkedin_url} onChange={e => set('linkedin_url', e.target.value)} />
            </Field>
          </div>

          <div style={SECTION}>Bize ilişkisi</div>
          <div className="form-row cols-2">
            <Field label="Tanışma tarihi *">
              <input type="date" value={form.first_met_date} onChange={e => set('first_met_date', e.target.value)} />
            </Field>
            <Field label="Tanışma kanalı">
              <input value={form.first_met_channel} onChange={e => set('first_met_channel', e.target.value)}
                placeholder="Google Meets, Yemek toplantısı…" />
            </Field>
          </div>
          <div className={`form-row ${isNetwork ? 'cols-2' : ''}`}>
            <Field label="Tanıtan *">
              <select value={form.introduced_by} onChange={e => set('introduced_by', e.target.value)}>
                <option value="">— seç —</option>
                {Object.entries(INTRODUCED_BY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            {isNetwork && (
              <Field label="Bize faydası *">
                <select value={form.relationship_type} onChange={e => set('relationship_type', e.target.value)}>
                  {Object.entries(RELATIONSHIP_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            )}
          </div>
          <div className="form-row">
            <Field label="Tanışma detayı">
              <textarea rows={2} style={{ resize: 'vertical' }}
                value={form.intro_detail} onChange={e => set('intro_detail', e.target.value)}
                placeholder="Nasıl tanıştık, kim aracılığıyla…" />
            </Field>
          </div>
          <div className="form-row">
            <Field label="Tipik cümle — bu kişiyi hatırlatan">
              <textarea rows={2} style={{ resize: 'vertical' }}
                value={form.typical_sentence} onChange={e => set('typical_sentence', e.target.value)}
                placeholder="40'larında, eski Foster ortağı, kendi projeleri için planning desteği arıyor" />
            </Field>
          </div>

          <div style={SECTION}>Sıradaki adım</div>
          <div className="form-row cols-2">
            <Field label="Aksiyon">
              <input value={form.next_action} onChange={e => set('next_action', e.target.value)}
                placeholder="Fee proposal gönder, £6–8k aralığı" />
            </Field>
            <Field label="Tarih">
              <input type="date" value={form.next_action_date} onChange={e => set('next_action_date', e.target.value)} />
            </Field>
          </div>

          {!isEdit && (
            <>
              <div style={SECTION}>İlk not (opsiyonel)</div>
              <div className="form-row cols-2">
                <Field label="Not tarihi">
                  <input type="date" value={noteDate} onChange={e => setNoteDate(e.target.value)} />
                </Field>
                <div />
              </div>
              <div className="form-row">
                <Field label="Not">
                  <textarea rows={2} style={{ resize: 'vertical' }}
                    value={noteText} onChange={e => setNoteText(e.target.value)}
                    placeholder="Toplantıda ne konuştuk…" />
                </Field>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          {error && (
            <span style={{ color: 'var(--red)', marginRight: 'auto', fontSize: 12, alignSelf: 'center' }}>
              {error}
            </span>
          )}
          <button className="btn-ghost" onClick={onClose} disabled={busy}>İptal</button>
          <button className="btn-primary" onClick={save} disabled={busy}>
            {busy ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
